import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Loader2, Plus, ShieldCheck, UserX, UserCheck, Users } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MIN_PASSWORD_LENGTH } from '@shipswift/shared';
import { api } from '@/api/client';
import { FieldError } from './form-utils';
import { errorClass, useFieldErrors } from './use-field-errors';


export default function UsersSection({ currentUser }) {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [resettingId, setResettingId] = useState(null);

  const { data: users = [], isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.users.list(),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['users'] });

  const banMutation = useMutation({
    mutationFn: ({ id, banned }) => (banned ? api.users.unban(id) : api.users.ban(id)),
    onSuccess: (_data, { banned }) => {
      invalidate();
      toast.success(banned ? 'Account re-enabled' : 'Account disabled');
    },
    onError: (error) => toast.error(error.message ?? 'Failed to update account'),
  });

  const roleMutation = useMutation({
    mutationFn: ({ id, role }) => api.users.update(id, { role }),
    onSuccess: () => {
      invalidate();
      toast.success('Role updated');
    },
    onError: (error) => toast.error(error.message ?? 'Failed to change role'),
  });

  return (
    <Card className="border-none shadow-lg">
      <CardHeader className="border-b bg-gradient-to-r from-slate-100 to-slate-50">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Users
          </CardTitle>
          <Button onClick={() => setShowForm(true)} size="sm" className="bg-slate-800 hover:bg-slate-900">
            <Plus className="w-4 h-4 mr-2" />
            Add user
          </Button>
        </div>
      </CardHeader>

      <CardContent className="p-6 space-y-4">
        {showForm && <NewUserForm onClose={() => setShowForm(false)} />}

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : (
          <div className="space-y-3">
            {users.map((user) => {
              const isSelf = user.id === currentUser.id;

              return (
                <div
                  key={user.id}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-lg border p-4"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-slate-900">{user.name}</span>
                      {user.role === 'admin' && (
                        <Badge className="bg-blue-100 text-blue-800 border-blue-300 gap-1 hover:bg-blue-100">
                          <ShieldCheck className="w-3 h-3" />
                          Admin
                        </Badge>
                      )}
                      {isSelf && <Badge variant="secondary">You</Badge>}
                      {user.banned && <Badge variant="destructive">Disabled</Badge>}
                    </div>
                    <p className="text-sm text-slate-500 truncate">{user.email}</p>
                  </div>

                  <div className="flex items-center gap-2">
                    <Select
                      value={user.role}
                      onValueChange={(role) => roleMutation.mutate({ id: user.id, role })}
                      disabled={roleMutation.isPending}
                    >
                      <SelectTrigger className="w-28 h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="staff">Staff</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setResettingId(user.id)}
                      className="gap-1"
                    >
                      <KeyRound className="w-4 h-4" />
                      Password
                    </Button>

                    {/* Disabling yourself would sign you out with no way back in,
                        so the control is not offered. The API refuses it too. */}
                    {!isSelf && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => banMutation.mutate({ id: user.id, banned: user.banned })}
                        disabled={banMutation.isPending}
                        className="gap-1"
                      >
                        {user.banned ? (
                          <>
                            <UserCheck className="w-4 h-4" />
                            Enable
                          </>
                        ) : (
                          <>
                            <UserX className="w-4 h-4" />
                            Disable
                          </>
                        )}
                      </Button>
                    )}
                  </div>

                  {resettingId === user.id && (
                    <div className="w-full">
                      <PasswordResetForm user={user} onClose={() => setResettingId(null)} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function NewUserForm({ onClose }) {
  const queryClient = useQueryClient();
  const { fieldErrors, handleError, clearErrors } = useFieldErrors();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'staff',
  });

  const createMutation = useMutation({
    mutationFn: (data) => api.users.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('User created');
      onClose();
    },
    onError: (error) => handleError(error, 'Failed to create user'),
  });

  const handleSubmit = (event) => {
    event.preventDefault();
    clearErrors();
    createMutation.mutate(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="rounded-lg border bg-slate-50 p-4 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="nu-name">Name</Label>
          <Input
            id="nu-name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className={errorClass(fieldErrors.name)}
            required
          />
          <FieldError message={fieldErrors.name} />
        </div>

        <div className="space-y-2">
          <Label htmlFor="nu-email">Email</Label>
          <Input
            id="nu-email"
            type="email"
            autoComplete="off"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className={errorClass(fieldErrors.email)}
            required
          />
          <FieldError message={fieldErrors.email} />
        </div>

        <div className="space-y-2">
          <Label htmlFor="nu-password">Temporary password</Label>
          <Input
            id="nu-password"
            type="text"
            autoComplete="new-password"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            className={errorClass(fieldErrors.password)}
            required
          />
          <FieldError message={fieldErrors.password} />
          <p className="text-xs text-slate-500">
            At least {MIN_PASSWORD_LENGTH} characters. Shown in the clear so you can hand it over.
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="nu-role">Role</Label>
          <Select
            value={formData.role}
            onValueChange={(role) => setFormData({ ...formData, role })}
          >
            <SelectTrigger id="nu-role">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="staff">Staff — can ship</SelectItem>
              <SelectItem value="admin">Admin — can also manage users</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" disabled={createMutation.isPending} className="bg-slate-800 hover:bg-slate-900">
          {createMutation.isPending ? 'Creating…' : 'Create user'}
        </Button>
      </div>
    </form>
  );
}

function PasswordResetForm({ user, onClose }) {
  const { fieldErrors, handleError, clearErrors } = useFieldErrors();
  const [password, setPassword] = useState('');

  const resetMutation = useMutation({
    mutationFn: (newPassword) => api.users.setPassword(user.id, newPassword),
    onSuccess: () => {
      toast.success(`Password reset for ${user.email}`);
      onClose();
    },
    onError: (error) => handleError(error, 'Failed to reset password'),
  });

  const handleSubmit = (event) => {
    event.preventDefault();
    clearErrors();
    resetMutation.mutate(password);
  };

  return (
    <form onSubmit={handleSubmit} className="mt-3 rounded-lg border bg-amber-50 p-3 space-y-2">
      <Label htmlFor={`pw-${user.id}`}>New password for {user.email}</Label>
      <div className="flex gap-2">
        <Input
          id={`pw-${user.id}`}
          type="text"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder={`At least ${MIN_PASSWORD_LENGTH} characters`}
          className={errorClass(fieldErrors.newPassword)}
          required
          autoFocus
        />
        <Button type="submit" disabled={resetMutation.isPending}>
          {resetMutation.isPending ? 'Setting…' : 'Set'}
        </Button>
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
      </div>
      <FieldError message={fieldErrors.newPassword} />
    </form>
  );
}
